import { PRACTICE_LEKCJE as LEKCJE } from './practice-course';
import { biezaca, numerKroku, odlozonaTeraz, postep, powtorkaNaTeraz, terminPowtorki, type StanNauki } from './silnik';
import { kiedy } from './czas';
import type { Przedmiot } from './typy';
import type { Tryb } from './FeedView';
import './nauka.css';
import { Math as Tex } from '@/components/Math';
import { TeacherStartup } from '@/features/ai/TeacherConnection';

/**
 * Nowa strona „Dziś”: jedna główna decyzja — „Kontynuuj”. Obok tylko
 * najbliższa powtórka. Statystyki i narzędzia są pod „Więcej”.
 */

interface Props {
  resume?: { title: string; onResume: () => void } | null;
  przedmiot: Przedmiot;
  przedmiotNazwa: string;
  stan: StanNauki | null;
  onStart: (skillId: string, tryb: Tryb) => void;
  onWiecej: () => void;
  onKurs: () => void;
  onTutor?: () => void;
  nextCourse: { id: string; name: string } | null;
  onCourseLesson: (skillId: string) => void;
  descriptions: Record<string, string>;
  /** Sprawdzian działu po przerobieniu jego podstawy. */
  sprawdzian?: { nazwa: string; onStart: () => void } | null;
  /** Nowy tryb nauki matematyki (demo) — tylko dla matematyki. */
  sesja?: { temat: string; opanowanie: number; wToku: boolean; onStart: () => void } | null;
}

export function DzisView({ przedmiot, przedmiotNazwa, stan, onStart, onWiecej, onKurs, onTutor, nextCourse, onCourseLesson, descriptions, sprawdzian = null, sesja, resume }: Props) {
  const teraz = Date.now();
  const lekcje = LEKCJE.filter((l) => l.przedmiot === przedmiot);
  const data = new Date(teraz).toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' });

  if (!stan) return <p className="boot">Wczytywanie…</p>;

  // Kolejność decyzji: zaległa powtórka > rozpoczęta lekcja > sprawdzian działu > nowa lekcja.
  // Lekcja odłożona po serii błędów czeka do jutra — nie wraca od razu.
  const zalegla = lekcje.find((l) => powtorkaNaTeraz(stan, l.skillId, teraz));
  const wToku = lekcje.find((l) => postep(stan, l).status === 'w trakcie' && !odlozonaTeraz(stan, l.skillId, teraz));
  const nowa = lekcje.find((l) => postep(stan, l).status === 'nowa');
  const cel = zalegla ? { l: zalegla, tryb: 'powtorka' as const } : wToku ? { l: wToku, tryb: 'nauka' as const } : !sprawdzian && nowa ? { l: nowa, tryb: 'nauka' as const } : null;

  const krok = (() => {
    if (!cel) return null;
    if (cel.tryb === 'powtorka') return 'Powtórka na innym zadaniu CKE';
    const k = biezaca(stan, cel.l);
    const nr = k ? numerKroku(stan, cel.l, k.id) : null;
    return nr ? `Krok ${nr.krok} z ${nr.z}` : null;
  })();

  const najblizsza = lekcje
    .map((l) => ({ l, t: terminPowtorki(stan, l.skillId) }))
    .filter((x): x is { l: (typeof lekcje)[number]; t: number } => x.t !== null)
    .sort((a, b) => a.t - b.t)[0];

  return (
    <main className="dzis">
      <header className="dzis__heading">
        <p className="dzis__data">{data}</p>
        <h1 className="dzis__tytul">Dziś · {przedmiotNazwa}</h1>
        <p className="dzis__subtitle">Jedna lekcja. Kolejny krok w Twoim tempie.</p>
      </header>

      <TeacherStartup />
      {resume && <section className="dzis__recommendation" aria-label="Przerwana lekcja">
        <p className="dzis__activity">Twoja praca jest zapisana</p>
        <h2 className="dzis__lesson">{resume.title}</h2>
        <p className="dzis__description">Wróć do tej samej karty, swojej odpowiedzi i zapisanych rachunków.</p>
        <button type="button" className="btn btn--primary dzis__start" onClick={resume.onResume}>Wznów przerwaną lekcję</button>
      </section>}

      {!cel && sprawdzian ? (
        <section className="dzis__recommendation" aria-label="Rekomendowana nauka">
          <p className="dzis__activity">Sprawdzian działu</p>
          <h2 className="dzis__lesson">{sprawdzian.nazwa}</h2>
          <p className="dzis__description">Podstawa tego działu za Tobą. Kilka zadań bez limitu czasu — słabsze miejsca wrócą w powtórkach.</p>
          <button type="button" className="btn btn--primary dzis__start" onClick={sprawdzian.onStart}>
            Zrób sprawdzian <span aria-hidden>→</span>
          </button>
        </section>
      ) : cel || nextCourse ? (
        <section className="dzis__recommendation" aria-label="Rekomendowana nauka">
          <p className="dzis__activity">{cel?.tryb === 'powtorka' ? 'Powtórka na dziś' : wToku && cel?.l === wToku ? 'Wracamy do lekcji' : 'Następna lekcja'}</p>
          <h2 className="dzis__lesson">{cel?.l.tytul ?? nextCourse!.name}</h2>
          <p className="dzis__description"><Tex>{descriptions[cel?.l.skillId ?? nextCourse!.id] ?? ''}</Tex></p>
          {krok && <p className="dzis__step">{krok}</p>}
          <button type="button" className="btn btn--primary dzis__start" onClick={() => cel ? onStart(cel.l.skillId, cel.tryb) : onCourseLesson(nextCourse!.id)}>
            {cel?.tryb === 'powtorka' ? 'Zrób powtórkę' : wToku && cel?.l === wToku ? 'Kontynuuj lekcję' : 'Rozpocznij lekcję'} <span aria-hidden>→</span>
          </button>
        </section>
      ) : <section className="dzis__recommendation"><h2 className="dzis__lesson">Dostępne lekcje przerobione</h2><p className="dzis__description">Możesz zakończyć naukę na dziś, wrócić do wybranej lekcji w kursie albo przećwiczyć materiał w treningu dodatkowym.</p><button className="btn" onClick={onKurs}>Otwórz kurs</button></section>}

      {/* Demo nowego trybu matematyki — drugorzędne, pod główną rekomendacją. */}
      {przedmiot === 'math' && onTutor && <section className="dzis__sesja dzis__sesja--mala"><div><span className="dzis__sesja-nad">FORGE AI Tutor · praca na kartce i analiza zdjęć</span><p>Nauczyciel dobierze dzisiejszą sesję do Twoich postępów.</p></div><button className="btn btn--primary" onClick={onTutor}>Rozpocznij korepetycje</button></section>}
      {sesja && (
        <button type="button" className="dzis__sesja dzis__sesja--mala" onClick={sesja.onStart}>
          <span className="dzis__sesja-nad">Kontynuuj matematykę · nowy tryb (demo) · {sesja.temat} · {sesja.opanowanie}%</span>
          <span className="dzis__sesja-cta">{sesja.wToku ? 'Kontynuuj' : 'Zacznij'} <span aria-hidden>→</span></span>
        </button>
      )}

      <p className="dzis__powtorka">
        {najblizsza
          ? `Najbliższa powtórka: ${najblizsza.l.tytul} — ${kiedy(najblizsza.t, teraz)}`
          : 'Najbliższa powtórka: pojawi się po pierwszej skończonej lekcji.'}
      </p>

      <div className="dzis__dol">
        <button type="button" className="dzis__link" onClick={onKurs}>
          Wszystkie lekcje w Kursie →
        </button>
        <button type="button" className="dzis__link" onClick={onWiecej}>Statystyki →</button>
      </div>
    </main>
  );
}
